import {
	OnboardingChecklist,
	type OnboardingChecklistResponse,
	UserAction,
} from "@brookbush/types";

import { useAuth } from "@clerk/nextjs";
import { useRouter } from "next/compat/router";
import useSWR from "swr";

import useNextRecommended from "./useNextRecommended";
import useOnboarding from "./useOnboarding";
import useSelectedCertification from "./useSelectedCertification";
import useUserAction from "./useUserAction";
import useTrackCTA from "~/hooks/useTrackCTA";
import fetcher from "~/utils/fetcher.client";

const useOnboardingChecklist = () => {
	const { userId } = useAuth();
	const router = useRouter();
	const {
		data: checklistResponse,
		isLoading: isLoadingChecklist,
		mutate,
	} = useSWR<OnboardingChecklistResponse>(userId && `/onboarding/checklist`);

	const checklistData = checklistResponse?.items;
	const isDismissed = checklistResponse?.isDismissed ?? false;

	const { selectedCertification } = useSelectedCertification();
	const { isOnboardingCompleted } = useOnboarding();

	const { course: recommendedCourse } = useNextRecommended(
		selectedCertification?.slug === "courses"
			? undefined
			: selectedCertification?.slug,
	);

	const { setUserAction } = useUserAction();
	const trackCTA = useTrackCTA();

	const checklist:
		| {
				title: string;
				key: OnboardingChecklist | "SETUP_ACCOUNT";
				isCompleted: boolean;
				handleClick: () => void;
		  }[]
		| undefined = checklistData && [
		// Hidden until onboarding is done — sends the user to the wizard to
		// finish setting up their account.
		...(isOnboardingCompleted
			? []
			: [
					{
						title: "Setup your account",
						key: "SETUP_ACCOUNT" as const,
						isCompleted: false,
						handleClick: async () => router?.push("/learning-plan"),
					},
				]),

		{
			title: "Become a Member",
			key: OnboardingChecklist.BECOME_A_MEMBER,
			isCompleted: !!checklistData?.includes(
				OnboardingChecklist.BECOME_A_MEMBER,
			),
			handleClick: async () => {
				trackCTA(UserAction.BECOME_A_MEMBER, "Onboarding Checklist");
				setUserAction(UserAction.BECOME_A_MEMBER);
			},
		},

		{
			title: "Get updates via text",
			key: OnboardingChecklist.ADD_PHONE_NUMBER,
			isCompleted: !!checklistData?.includes(
				OnboardingChecklist.ADD_PHONE_NUMBER,
			),
			handleClick: async () => setUserAction(UserAction.OPEN_PHONE_NUMBER),
		},

		{
			title: "Start First Lesson",
			key: OnboardingChecklist.START_FIRST_COURSE,

			isCompleted: !!checklistData?.includes(
				OnboardingChecklist.START_FIRST_COURSE,
			),
			handleClick: async () =>
				recommendedCourse
					? router?.push(`/courses/${recommendedCourse?.slug}`)
					: router?.push("/courses"),
		},

		{
			title: "Generate First Program",
			key: OnboardingChecklist.GENERATE_FIRST_PROGRAM,

			isCompleted: !!checklistData?.includes(
				OnboardingChecklist.GENERATE_FIRST_PROGRAM,
			),

			handleClick: async () => router?.push("/programs"),
		},

		{
			title: "Refer a Friend & Earn",
			key: OnboardingChecklist.REFER_A_FRIEND,

			isCompleted: !!checklistData?.includes(
				OnboardingChecklist.REFER_A_FRIEND,
			),

			handleClick: async () => router?.push("/referrals"),
		},
	];

	const currentItem = checklist?.find((item) => !item.isCompleted);

	const numberOfRemainingSteps = checklist?.filter(
		(item) => !item.isCompleted,
	).length;

	const handleCompleteChecklistItem = async (key: OnboardingChecklist) => {
		if (checklistData?.includes(key)) return;

		await mutate(
			fetcher(`/onboarding/checklist/${key}`, {
				method: "PUT",
			}),
			{
				revalidate: false,
				populateCache: (_, oldData) => ({
					isDismissed: oldData?.isDismissed ?? false,
					items: [...(oldData?.items ?? []), key],
				}),
			},
		);
	};

	const dismissChecklist = async () => {
		await mutate(
			fetcher(`/onboarding/checklist`, {
				method: "DELETE",
			}),
			{
				revalidate: false,
				populateCache: (_, oldData) => ({
					items: oldData?.items ?? [],
					isDismissed: true,
				}),
			},
		);
	};

	const currentIndex = checklist?.findIndex((item) => !item.isCompleted);

	return {
		checklist,
		currentItem,
		currentIndex,
		isLoadingChecklist,
		isDismissed,
		handleCompleteChecklistItem,
		dismissChecklist,
		numberOfRemainingSteps,
	};
};

export default useOnboardingChecklist;
